import React from 'react';
import { FiFilter, FiX, FiCalendar } from 'react-icons/fi';
import { POST_STATUSES } from '../../services/SocialPostService';

const STATUS_LABELS = {
  draft: 'Drafts',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  queued: 'Queued',
  publishing: 'Publishing',
  posted: 'Posted',
  failed: 'Failed',
  canceled: 'Canceled',
};

const QUICK_RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
];

function getDateRange(rangeKey) {
  const now = new Date();
  const pstNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const startOfDay = new Date(pstNow);
  startOfDay.setHours(0, 0, 0, 0);

  switch (rangeKey) {
    case 'today':
      return { from: startOfDay.toISOString(), to: new Date(startOfDay.getTime() + 86400000).toISOString() };
    case 'week': {
      const day = pstNow.getDay();
      const startOfWeek = new Date(startOfDay);
      startOfWeek.setDate(startOfWeek.getDate() - day);
      const endOfWeek = new Date(startOfWeek.getTime() + 7 * 86400000);
      return { from: startOfWeek.toISOString(), to: endOfWeek.toISOString() };
    }
    case 'month': {
      const startOfMonth = new Date(pstNow.getFullYear(), pstNow.getMonth(), 1);
      const endOfMonth = new Date(pstNow.getFullYear(), pstNow.getMonth() + 1, 0, 23, 59, 59);
      return { from: startOfMonth.toISOString(), to: endOfMonth.toISOString() };
    }
    default:
      return { from: null, to: null };
  }
}

export default function PostFilters({
  filters = {},
  onChange,
  onClear,
  className = '',
}) {
  const { status, platform, from, to, dateRange } = filters;
  const hasFilters = status || platform || from || to;

  const handleStatusChange = (s) => {
    onChange?.({ status: status === s ? null : s, dateRange: null });
  };

  const handleDateRange = (rangeKey) => {
    if (dateRange === rangeKey) {
      onChange?.({ from: null, to: null, dateRange: null });
    } else {
      const range = getDateRange(rangeKey);
      onChange?.({ ...range, dateRange: rangeKey });
    }
  };

  const handlePlatformChange = (p) => {
    onChange?.({ platform: platform === p ? null : p });
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Status pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <FiFilter className="w-4 h-4 text-zinc-400 flex-shrink-0" />
        <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider mr-1">Status</span>
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => handleStatusChange(key)}
            className={`
              px-2.5 py-1 rounded-lg text-xs font-medium transition-all
              ${status === key
                ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200'
                : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'}
            `}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Date range pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <FiCalendar className="w-4 h-4 text-zinc-400 flex-shrink-0" />
        <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider mr-1">Date</span>
        {QUICK_RANGES.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleDateRange(key)}
            className={`
              px-2.5 py-1 rounded-lg text-xs font-medium transition-all
              ${dateRange === key
                ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200'
                : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'}
            `}
          >
            {label}
          </button>
        ))}

        {/* Platform filter */}
        <span className="ml-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Platform</span>
        {['linkedin'].map((p) => (
          <button
            key={p}
            onClick={() => handlePlatformChange(p)}
            className={`
              px-2.5 py-1 rounded-lg text-xs font-medium transition-all capitalize
              ${platform === p
                ? 'bg-[#0A66C2]/10 text-[#0A66C2] ring-1 ring-[#0A66C2]/20'
                : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'}
            `}
          >
            {p}
          </button>
        ))}

        {/* Account filter */}
        <span className="ml-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Account</span>
        {['personal', 'business'].map((a) => (
          <button
            key={a}
            onClick={() => onChange?.({ account: filters.account === a ? null : a })}
            className={`
              px-2.5 py-1 rounded-lg text-xs font-medium transition-all capitalize
              ${filters.account === a
                ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200'
                : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'}
            `}
          >
            {a}
          </button>
        ))}

        {/* Clear */}
        {hasFilters && (
          <button
            onClick={onClear}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 transition-colors ml-2"
          >
            <FiX className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
    </div>
  );
}
